import React, { useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TranscriptEntryEditor } from "./TranscriptEntryEditor";
import { EntryStatusBanner } from "./EntryStatusBanner";
import { AddEntryDivider } from "./AddEntryDivider";
import { NewEntryForm } from "./NewEntryForm";
import {
  useTranscriptActions,
  useTranscriptDraft,
} from "../hooks/useTranscriptDraft";
import {
  entryValidation,
  TranscriptValidationResult,
} from "../utils/transcriptValidation";
import { formatTimestamp, paragraphText } from "../utils/timeFormat";
import { formatSpeakerLabel } from "./speakerColors";

interface TranscriptEntryListProps {
  currentTime: number;
  seekTo: (t: number) => void;
  disabled?: boolean;
  validation: TranscriptValidationResult;
  armedUid: string | null;
  /** True while any range is being set on the timeline (disables inserts). */
  armingActive: boolean;
  onArmEntry: (uid: string, start: number, end: number) => void;
  /** Begin adding a new entry whose start is fixed; the user picks the end. */
  onArmNewAt: (start: number, speaker: string, text: string) => void;
}

/**
 * Renders the editable transcript entries from the draft store. Tracks the
 * active entry for the current playback time, surfaces per-entry validation
 * (word-gate / invalid range / empty text), and hosts the delete-entry dialog.
 */
export const TranscriptEntryList: React.FC<TranscriptEntryListProps> = ({
  currentTime,
  seekTo,
  disabled = false,
  validation,
  armedUid,
  armingActive,
  onArmEntry,
  onArmNewAt,
}) => {
  const { paragraphs, speakers, originalByUid } = useTranscriptDraft();
  const {
    reassignSpeaker,
    verifySpeaker,
    editText,
    deleteEntry,
    deleteSentence,
    splitSentenceToSpeaker,
  } = useTranscriptActions();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  // uid of the entry after which an inline "add entry" form is open (or null).
  const [composingAfter, setComposingAfter] = useState<string | null>(null);

  const activeUid = useMemo(() => {
    const active = paragraphs.find(
      (p) => p.start <= currentTime && currentTime < p.end,
    );
    return active?._uid ?? null;
  }, [paragraphs, currentTime]);

  const lastScrolledUid = useRef<string | null>(null);

  const pendingEntry = paragraphs.find((p) => p._uid === pendingDelete) ?? null;

  return (
    <>
      <Box>
        {paragraphs.map((entry, index) => {
          const uid = entry._uid as string;
          const isActive = uid === activeUid || uid === armedUid;
          const ev = entryValidation(validation, uid);
          const attention =
            ev.invalidRange || ev.emptyText
              ? ("error" as const)
              : ev.needsRangeReselect
                ? ("warning" as const)
                : undefined;
          return (
            <React.Fragment key={uid}>
              {index > 0 && <Box sx={{ height: 4 }} />}
              <div
                // First entry is the walkthrough's spotlight target for the
                // edit-text / delete-sentence / change-speaker steps.
                data-tour={index === 0 ? "entry" : undefined}
                // Keep the active entry clear of the sticky "Unsaved changes"
                // bar (bottom:16, ~72px tall) and the fixed app header during
                // auto-scroll — scrollIntoView honors scroll-margin.
                style={{ scrollMarginBottom: 120, scrollMarginTop: 96 }}
                ref={(el) => {
                  if (isActive && el && lastScrolledUid.current !== uid) {
                    lastScrolledUid.current = uid;
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                  }
                }}
              >
                <TranscriptEntryEditor
                  entry={entry}
                  speakers={speakers}
                  isActive={isActive}
                  originalSentences={originalByUid.get(uid)}
                  disabled={disabled}
                  attention={attention}
                  onSeek={seekTo}
                  onReassignSpeaker={(label) => reassignSpeaker(uid, label)}
                  onVerifySpeaker={(sIdx) => verifySpeaker(uid, sIdx)}
                  onSentenceChange={(sIdx, text) => editText(uid, sIdx, text)}
                  onSplitSentence={(sIdx, label) =>
                    splitSentenceToSpeaker(uid, sIdx, label)
                  }
                  onDeleteSentence={(sIdx) => deleteSentence(uid, sIdx)}
                  onRequestDelete={() => setPendingDelete(uid)}
                  statusBanner={
                    <EntryStatusBanner
                      validation={ev}
                      onSetRange={() => onArmEntry(uid, entry.start, entry.end)}
                    />
                  }
                />
              </div>
              {/* Insert a new entry starting right after this one. */}
              {composingAfter === uid && !armingActive ? (
                <Box sx={{ my: 1 }}>
                  <NewEntryForm
                    speakers={speakers}
                    defaultSpeaker={String(entry.speaker)}
                    title="Insert entry here"
                    onSubmit={(speaker, text) => {
                      setComposingAfter(null);
                      onArmNewAt(entry.end, speaker, text);
                    }}
                    onCancel={() => setComposingAfter(null)}
                  />
                </Box>
              ) : (
                <AddEntryDivider
                  disabled={disabled || armingActive}
                  onClick={() => setComposingAfter(uid)}
                  dataTour={index === 0 ? "add-divider" : undefined}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteEntry(pendingDelete);
          setPendingDelete(null);
        }}
        tone="error"
        icon={<DeleteOutlineIcon />}
        title="Delete this transcript entry?"
        description={
          pendingEntry
            ? `${formatSpeakerLabel(pendingEntry.speaker)} · ${formatTimestamp(
                pendingEntry.start,
              )} — "${paragraphText(pendingEntry).slice(0, 80)}${
                paragraphText(pendingEntry).length > 80 ? "…" : ""
              }"`
            : undefined
        }
        confirmLabel="Delete entry"
        confirmStartIcon={<DeleteOutlineIcon />}
      />
    </>
  );
};
